import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const ImageFeatureSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden" id="about">
      <div className="container py-16 lg:py-24 relative mx-auto px-4">
        <div className="flex flex-wrap -mx-4 items-center justify-center">
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={isVisible ? { x: 0, opacity: 1 } : { x: -100, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="order-2 lg:order-1 w-full md:w-2/5 px-4 mb-8 lg:mb-0"
          >
            <div className="relative">
              <img
                src="/images/feature.jpg"
                alt="Feature"
                className="w-full rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-custom-indigo-500 rounded-2xl opacity-20"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-orange-500 rounded-full opacity-20"></div>
            </div>
          </motion.div>

          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={isVisible ? { x: 0, opacity: 1 } : { x: 100, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="order-1 lg:order-2 w-full md:w-3/5 md:pl-16 lg:pl-24 px-4"
          >
            <div className="mb-6">
              <span className="text-gray-500 dark:text-gray-400 font-medium text-lg">Why Choose Us</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl leading-tight font-medium mb-6">
              Make the right way the easy way
            </h2>

            <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-8">
              Headquartered in Mexico City, we collaborate in the same hours as our clients in Mexico and the United States. For
              local customers, that means Spanish‑first communication, familiarity with Mexican regulatory and payment
              landscapes, and the ability to be on‑site when it matters. For U.S. organizations, it means senior engineers
              present for real‑time decisions without late night standups or multi‑day feedback loops
            </p>  
            
            <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-8">
              Our teams are senior, compact, and
              accountable. We measure success in production outcomes: reduced handling time, faster cycle time,
              higher conversion, lower incident rates. We design every engagement to transfer capabilities, not
              create dependency.
            </p>

            <div className="space-y-4 mb-8">
              {[
                "Average 10+ years experience across the team",
                "Senior innovators from companies like Amazon, Citibank, Red Hat",
                "Full-stack capabilities: front-end, back-end, data, AI, security",
                "Product and design thinking, not just engineering execution"
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ x: 50, opacity: 0 }}
                  animate={isVisible ? { x: 0, opacity: 1 } : { x: 50, opacity: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="flex items-center"
                >
                  <div className="w-2 h-2 bg-custom-indigo-500 rounded-full mr-4"></div>
                  <span className="text-gray-700 dark:text-gray-300">{item}</span>
                </motion.div>
              ))}
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ImageFeatureSection;